import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  IconButton,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Text,
  VStack,
  HStack,
  Badge,
  Skeleton,
} from '@chakra-ui/react';
import { MdClose, MdFavorite, MdFavoriteBorder, MdPlayArrow } from 'react-icons/md';
import type { EpisodeItem, SeriesDetail, SeriesItem } from '../types';
import {
  streamGhostOnDarkHover,
  streamGhostPinkHover,
  streamOutlineOnDarkHover,
  streamPinkOutlineHover,
} from '../darkThemeButtonProps';

interface Props {
  series: SeriesItem | null;
  detail: SeriesDetail | null;
  open: boolean;
  onClose: () => void;
  onEnsureLoaded: (s: SeriesItem) => Promise<void>;
  isFavoriteSeries: boolean;
  onToggleFavoriteSeries: () => void;
  isFavoriteEpisode: (episodeId: string) => boolean;
  onToggleFavoriteEpisode: (ep: EpisodeItem) => void;
  onPlayEpisode: (ep: EpisodeItem) => void;
  /** Desktop right panel — no modal chrome */
  embedded?: boolean;
}

function SeriesBody({
  series,
  detail,
  loading,
  season,
  setSeason,
  seasonEpisodes,
  isFavoriteSeries,
  onToggleFavoriteSeries,
  isFavoriteEpisode,
  onToggleFavoriteEpisode,
  onPlayEpisode,
  onClose,
  embedded,
}: {
  series: SeriesItem;
  detail: SeriesDetail | null;
  loading: boolean;
  season: number | null;
  setSeason: (n: number) => void;
  seasonEpisodes: EpisodeItem[];
  isFavoriteSeries: boolean;
  onToggleFavoriteSeries: () => void;
  isFavoriteEpisode: (episodeId: string) => boolean;
  onToggleFavoriteEpisode: (ep: EpisodeItem) => void;
  onPlayEpisode: (ep: EpisodeItem) => void;
  onClose: () => void;
  embedded?: boolean;
}) {
  return (
    <VStack align="stretch" spacing={3}>
      <HStack align="flex-start" spacing={3}>
        <Box w={embedded ? '72px' : '100px'} flexShrink={0} borderRadius="md" overflow="hidden" bg="whiteAlpha.100">
          {series.poster ? (
            <Image src={series.poster} alt="" w="100%" objectFit="cover" />
          ) : (
            <Flex h={embedded ? '100px' : '140px'} align="center" justify="center" fontSize="xs" color="teal.200" px={1} textAlign="center">
              {series.name}
            </Flex>
          )}
        </Box>
        <VStack align="stretch" spacing={1} flex={1} minW={0}>
          <HStack justify="space-between" align="flex-start">
            <Text fontSize={embedded ? 'sm' : 'lg'} fontWeight="700" color="white" noOfLines={2}>
              {series.name}
            </Text>
            {embedded && (
              <IconButton
                aria-label="Close"
                icon={<MdClose />}
                size="xs"
                variant="ghost"
                color="whiteAlpha.600"
                {...streamGhostOnDarkHover}
                onClick={onClose}
              />
            )}
          </HStack>
          <Text fontSize="xs" color="whiteAlpha.500">
            {[series.year, series.genre].filter(Boolean).join(' · ')}
          </Text>
          <IconButton
            aria-label="Favorite series"
            icon={isFavoriteSeries ? <MdFavorite /> : <MdFavoriteBorder />}
            size="xs"
            variant="outline"
            borderColor="whiteAlpha.300"
            color={isFavoriteSeries ? 'pink.300' : 'white'}
            {...(isFavoriteSeries ? streamPinkOutlineHover : streamOutlineOnDarkHover)}
            onClick={onToggleFavoriteSeries}
            alignSelf="flex-start"
          />
        </VStack>
      </HStack>
      {loading && <Skeleton height="80px" borderRadius="md" />}
      {!loading && detail && (
        <>
          <HStack spacing={1} overflowX="auto" pb={1} flexWrap="wrap">
            {detail.seasons.map((s) => (
              <Button
                key={s.seasonNumber}
                size="xs"
                h="24px"
                borderRadius="full"
                variant={season === s.seasonNumber ? 'solid' : 'outline'}
                colorScheme="teal"
                color={season === s.seasonNumber ? 'gray.900' : 'whiteAlpha.800'}
                borderColor="whiteAlpha.300"
                {...(season !== s.seasonNumber ? streamOutlineOnDarkHover : {})}
                onClick={() => setSeason(s.seasonNumber)}
              >
                S{s.seasonNumber}
              </Button>
            ))}
          </HStack>
          <VStack align="stretch" spacing={1}>
            {seasonEpisodes.map((ep) => (
              <Flex
                key={ep.id}
                p={2}
                borderRadius="md"
                bg="whiteAlpha.50"
                borderWidth="1px"
                borderColor="whiteAlpha.100"
                align="center"
                gap={2}
              >
                <Badge colorScheme="teal" fontSize="9px">
                  E{ep.episodeNumber}
                </Badge>
                <Box flex={1} minW={0}>
                  <Text fontWeight="600" color="white" fontSize="sm" noOfLines={1}>
                    {ep.title}
                  </Text>
                </Box>
                <IconButton
                  aria-label="Favorite episode"
                  icon={isFavoriteEpisode(ep.id) ? <MdFavorite /> : <MdFavoriteBorder />}
                  size="xs"
                  variant="ghost"
                  color={isFavoriteEpisode(ep.id) ? 'pink.300' : 'whiteAlpha.500'}
                  {...(isFavoriteEpisode(ep.id) ? streamGhostPinkHover : streamGhostOnDarkHover)}
                  onClick={() => onToggleFavoriteEpisode(ep)}
                />
                <Button
                  size="xs"
                  colorScheme="teal"
                  leftIcon={<MdPlayArrow />}
                  onClick={() => onPlayEpisode(ep)}
                  isDisabled={!ep.streamUrl}
                >
                  Play
                </Button>
              </Flex>
            ))}
          </VStack>
        </>
      )}
      {!loading && !detail && (
        <Text color="whiteAlpha.500" fontSize="sm">
          No episode data loaded yet.
        </Text>
      )}
    </VStack>
  );
}

export function SeriesBrowser({
  series,
  detail,
  open,
  onClose,
  onEnsureLoaded,
  isFavoriteSeries,
  onToggleFavoriteSeries,
  isFavoriteEpisode,
  onToggleFavoriteEpisode,
  onPlayEpisode,
  embedded,
}: Props) {
  const [loading, setLoading] = useState(false);
  const [season, setSeason] = useState<number | null>(null);

  useEffect(() => {
    if (!open || !series) return;
    setLoading(true);
    onEnsureLoaded(series).finally(() => setLoading(false));
  }, [open, series, onEnsureLoaded]);

  useEffect(() => {
    if (detail?.seasons?.length) {
      setSeason(detail.seasons[0].seasonNumber);
    } else {
      setSeason(null);
    }
  }, [detail]);

  if (!series || !open) return null;

  const seasonEpisodes = detail?.seasons.find((s) => s.seasonNumber === season)?.episodes ?? [];

  if (embedded) {
    return (
      <SeriesBody
        series={series}
        detail={detail}
        loading={loading}
        season={season}
        setSeason={setSeason}
        seasonEpisodes={seasonEpisodes}
        isFavoriteSeries={isFavoriteSeries}
        onToggleFavoriteSeries={onToggleFavoriteSeries}
        isFavoriteEpisode={isFavoriteEpisode}
        onToggleFavoriteEpisode={onToggleFavoriteEpisode}
        onPlayEpisode={onPlayEpisode}
        onClose={onClose}
        embedded
      />
    );
  }

  return (
    <Modal isOpen={open} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.800" />
      <ModalContent bg="#121a22" borderWidth="1px" borderColor="whiteAlpha.100" mx={3}>
        <ModalCloseButton color="white" zIndex={1} />
        <ModalBody pb={6} pt={10}>
          <SeriesBody
            series={series}
            detail={detail}
            loading={loading}
            season={season}
            setSeason={setSeason}
            seasonEpisodes={seasonEpisodes}
            isFavoriteSeries={isFavoriteSeries}
            onToggleFavoriteSeries={onToggleFavoriteSeries}
            isFavoriteEpisode={isFavoriteEpisode}
            onToggleFavoriteEpisode={onToggleFavoriteEpisode}
            onPlayEpisode={onPlayEpisode}
            onClose={onClose}
          />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
